/**
 * Procurement Demo — makes Nami available on the procurement demo pages.
 *
 * THROWAWAY DEMO CODE. The floating chat panel only opens when a page has
 * registered an agent module, and none of the procurement demo pages do. This
 * registers a tool-less module (the scripted interceptor short-circuits the
 * backend, so no tools are needed) on the procurement demo pages, with or without
 * demo mode, since those pages only ever show demo data. It also gives the empty chat the questions that
 * fit the current page, and opens the "Sources" links in Nami's answers inside
 * the app so the conversation is not lost. The pages that used to have their own
 * "Ask" box (contracts list, bid page, supplier, material, replenishment) offer
 * their questions here. Mounted by `ChatAgentRenderer`.
 * Delete with src/demo/procurement.
 */

import { useMemo } from 'react';
import { useEventListener } from '@react-hookz/web';
import { useLocation, useRouter } from '@tanstack/react-router';
import type { ModuleKey } from '@erp/rpc';
import { useAgentContainer } from '@/context/agent-container.context';
import { useAgentModule } from '@/hooks/useAgentModule';
import type { FrontendToolDefinition } from '@/hooks/useFrontendTool';
import { isProcurementDemoPath } from './demo-paths';
import { suggestionsForPath } from './suggestions';

const DEMO_MODULE_KEY: ModuleKey = 'general';
const NO_TOOLS: FrontendToolDefinition[] = [];
const IN_APP_LINK_PREFIX = '/procurement/';

interface RegistrarProps {
  pathname: string;
}

function Registrar({ pathname }: RegistrarProps) {
  const router = useRouter();
  const { containerElement } = useAgentContainer();
  // The same list for the same page, so the chat is not re-registered on every render.
  const suggestions = useMemo(() => suggestionsForPath(pathname), [pathname]);

  useAgentModule({
    moduleKey: DEMO_MODULE_KEY,
    currentPage: 'procurement-demo',
    tools: NO_TOOLS,
    headerTitle: 'Nami — Procurement',
    placeholder: 'Ask Nami about procurement...',
    suggestions,
  });

  // A "Sources" link in an answer is a plain anchor. Following it as a page
  // load would reset the chat, so open it through the router instead.
  useEventListener(document, 'click', (event: MouseEvent) => {
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey ||
      !(event.target instanceof Element)
    ) {
      return;
    }
    const anchor = event.target.closest('a');
    if (!anchor || !containerElement?.contains(anchor)) return;
    const href = anchor.getAttribute('href');
    if (!href?.startsWith(IN_APP_LINK_PREFIX)) return;
    event.preventDefault();
    router.history.push(href);
  });

  return null;
}

export function ProcurementNamiRegistrar() {
  const { pathname } = useLocation();
  if (!isProcurementDemoPath(pathname)) return null;
  return <Registrar pathname={pathname} />;
}
